<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1>All Locations</h1>
      </v-col>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            Latest Entries
            <v-spacer />
            <v-text-field v-model="search"
                          append-icon="search"
                          label="Search"
                          single-line
                          hide-details
                          @input="page = 1" />
          </v-card-title>
          <v-data-table class="elevation-1"
                        sort-by="CreatedOn"
                        sort-desc
                        :disable-pagination="items.length < 10"
                        :hide-default-footer="items.length < 10"
                        :loading="loading"
                        :headers="headers"
                        :items="items"
                        :footer-props="{
                          itemsPerPageOptions: [],
                          showFirstLastPage: true,
                        }"
                        :search="search"
                        :page.sync="page">
            <template v-slot:item.PhotoId="{ item }">
              <v-img width="30px"
                     height="30px"
                     :src="getImage(item.Id, 'Location', 'Location.jpg')" />
            </template>
            <template v-slot:item.CreatedOn="{ item }">
              {{ friendlyDate(item.CreatedOn) }}
            </template>
            <template v-slot:item.Active="{ item }">
              <v-switch @change="$refs.confirmActivation.open({ value: $event, target: item })"
                        v-model="item.Active"
                        :disabled="!$store.getters.can('Editor')">
                {{ item.Active ? 'Active' : 'Inactive' }}
              </v-switch>
            </template>
            <template v-slot:item.HideLocation="{ item }">
              <v-switch @change="$refs.confirmHideLocation.open({ value: $event, target: item })"
                        v-model="item.HideLocation"
                        :disabled="!$store.getters.can('Editor')">
                {{ item.HideLocation ? 'Hide Location' : 'Show Location' }}
              </v-switch>
            </template>
            <template v-slot:item.Status="{ item }">
              {{ friendlyStatus(item.Status) }}
            </template>
            <template v-slot:item.Actions="{ item }">
              <v-tooltip top :activator="`#editBtn-${item.Id}`">
                {{ $store.getters.can('Editor') || item.Status === 0
                ? 'Edit'
                : 'View' }}
              </v-tooltip>
              <v-tooltip top :activator="`#deleteBtn-${item.Id}`">
                Delete
              </v-tooltip>

              <v-btn icon link
                     color="accent"
                     :id="`editBtn-${item.Id}`"
                     :to="{ name: 'location.update', params: { id: item.Id } }">
                <v-icon>
                  {{ $store.getters.can('Editor') || item.Status === 0
                  ? 'edit'
                  : 'visibility' }}
                </v-icon>
              </v-btn>
              <v-btn icon link
                     color="accent"
                     v-if="$store.getters.can('Admin')"
                     :id="`deleteBtn-${item.Id}`"
                     @click="$refs.confirmDelete.open(item)">
                <v-icon>delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-col cols="12" class="text-right">
        <v-btn color="primary" dark link :to="{ name: 'location.create' }">
          <v-icon dark left>add</v-icon>
          Create
        </v-btn>
      </v-col>
    </v-row>
    <v-row justify="center" align="center">
      <ConfirmDialog ref="confirmActivation"
                     title="CONFIRM LOCATION UPDATE"
                     width="450"
                     @cancel="$event.target.Active = !$event.target.Active"
                     @continue="updateActiveState($event)">
        <template v-slot:default="{ item }">
          This action will <strong>{{ item && item.value ? 'Activate' : 'Deactivate' }}</strong>
          the selected Location<br><br>
          <span v-if="item && item.value">
            This record will now be available to general consumers of the FAP API<br>
          </span>
          <span v-else>
            This record will no longer be available to general consumers of the FAP API<br>
            You may still make changes to this record within the FAP UI
            and reactivate when ready<br>
          </span>
          <strong>Are you sure you would like to proceed?</strong>
        </template>
      </ConfirmDialog>
      <ConfirmDialog ref="confirmDelete"
                     title="WARNING"
                     width="450"
                     @continue="deleteItem($event)">
        This action will permanently delete this location
        and remove all references from Providers that may use it.<br>
        <strong>Are you sure you would like to proceed?</strong>
      </ConfirmDialog>
      <ConfirmDialog ref="confirmHideLocation"
                     title="CONFIRM SHOW/HIDE LOCATION UPDATE"
                     width="450"
                     @cancel="$event.target.HideLocation = !$event.target.HideLocation"
                     @continue="updateHideLocationState($event)">
        <template v-slot:default="{ item }">
     This action will <strong>{{ item && item.value ? 'Hide' : 'Show' }}</strong>
          the selected Location<br><br>
          <span v-if="item && item.value">
            This record will now be available to general consumers of the FAP API<br>
          </span>
          <span v-else>
            This record will no longer be available to general consumers of the FAP API<br>
            You may still make changes to this record within the FAP UI
            and reactivate when ready<br>
          </span>
          <strong>Are you sure you would like to proceed?</strong>
        </template>
      </ConfirmDialog>

    </v-row>
  </v-container>
</template>

<script>
import FriendlyFields from '@/components/Mixins/FriendlyFields';

export default {
  name: 'LocationIndex',
  mixins: [
    FriendlyFields,
  ],
  data() {
    return {
      headers: [
        { text: 'Id', value: 'Id' },
        {
          text: 'Photo',
          value: 'PhotoId',
          filterable: false,
          sortable: false,
        },
        { text: 'Title', value: 'Title' },
        { text: 'Created On', value: 'CreatedOn', filterable: false },
        { text: 'Is Active', value: 'Active', filterable: false },
        { text: 'Hide Location', value: 'HideLocation', filterable: false },
        { text: 'Status', value: 'Status' },
        {
          text: 'Actions',
          value: 'Actions',
          sortable: false,
          filterable: false,
        },
      ],
      items: [],
      loading: true,
      search: '',
      page: 1,
    };
  },
  methods: {
    getImage(id, directory, notfound = null) {
      return `${process.env.VUE_APP_API}/Media/${directory}/${id}${notfound ? `?notfound=${notfound}` : ''}`;
    },
    updateActiveState({ value, target }) {
      this.$store.dispatch('patch', {
        api: `Location/${target.Id}/Activate`,
        data: { '': value },
      });
    },
    updateHideLocationState({ value, target }) {
      this.$store.dispatch('patch', {
        api: `Location/${target.Id}/ToggleHide`,
        data: { '': value },
      });
    },
    async deleteItem(item) {
      try {
        const i = this.items.indexOf(item);
        await this.$store.dispatch('delete', `Location/${item.Id}`);
        this.items.splice(i, 1);
      } catch (e) {
        // Do nothing
      }
    },
  },
  async created() {
    const response = await this.$store.dispatch('get', 'Location');
    this.items = response.data.Items;
    this.loading = false;
  },
};
</script>
